/**
 * Haptics and keep-awake: what the body feels and whether the screen stays on.
 */

import { isNative } from "./platform";

export type ImpactWeight = "heavy" | "light" | "medium";

const VIBRATE_MS: Record<ImpactWeight, number> = {
  heavy: 30,
  light: 10,
  medium: 20,
};

/**
 * One tap of the haptic motor. Resolves `true` when something was asked to
 * vibrate. Web: `navigator.vibrate`, which Android Chrome honours and desktop
 * and iOS Safari ignore — `false` there, never a throw.
 */
export async function impact(
  weight: ImpactWeight = "medium"
): Promise<boolean> {
  try {
    if (!isNative()) {
      return typeof navigator.vibrate === "function"
        ? navigator.vibrate(VIBRATE_MS[weight])
        : false;
    }
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const style = {
      heavy: ImpactStyle.Heavy,
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
    }[weight];
    await Haptics.impact({ style });
    return true;
  } catch (error) {
    console.warn("[native] haptic impact failed", error);
    return false;
  }
}

// The web Wake Lock sentinel. A browser drops it on its own when the tab is
// hidden, so `isKeptAwake` reads its `released` flag rather than trusting a
// boolean of ours.
let webWakeLock: WakeLockSentinel | null = null;

/**
 * Keeps the screen on — a workout in progress. Native: the plugin (a window
 * flag). Web: the Screen Wake Lock API, which needs a visible tab and a secure
 * origin; `false` where it is refused or absent.
 */
export async function keepAwake(): Promise<boolean> {
  try {
    if (!isNative()) {
      if (!("wakeLock" in navigator)) {
        return false;
      }
      webWakeLock = await navigator.wakeLock.request("screen");
      return true;
    }
    const { KeepAwake } = await import("@capacitor-community/keep-awake");
    await KeepAwake.keepAwake();
    return true;
  } catch (error) {
    console.warn("[native] keep awake failed", error);
    return false;
  }
}

export async function allowSleep(): Promise<void> {
  try {
    if (!isNative()) {
      await webWakeLock?.release();
      webWakeLock = null;
      return;
    }
    const { KeepAwake } = await import("@capacitor-community/keep-awake");
    await KeepAwake.allowSleep();
  } catch (error) {
    console.warn("[native] allow sleep failed", error);
  }
}

export async function isKeptAwake(): Promise<boolean> {
  try {
    if (!isNative()) {
      return webWakeLock !== null && !webWakeLock.released;
    }
    const { KeepAwake } = await import("@capacitor-community/keep-awake");
    return (await KeepAwake.isKeptAwake()).isKeptAwake;
  } catch {
    return false;
  }
}
